import apiClient from './api';
import { validateRequiredParams } from '@/utils/apiUtils';
import {
  ShareOfMetricsResponse,
  ShareOfMetricsData,
  ShareOfMetricsParams,
  SocialMetricsComparisonResponse,
  SocialMetricsComparisonParams,
  BrandMetrics,
} from '@/types/media.types';

/** BigQuery suele exponer `SUM(likes) AS total_likes`, etc.: mismo nombre de serie + prefijo `total_`. */
function bigQueryTotalFieldName(metricKey: keyof SocialMetricsComparisonResponse): string {
  return `total_${metricKey}`;
}

function pickSocialMetricsSeriesLabel(row: Record<string, unknown>): string {
  const candidates = [row.brand, row.hashtag_name, row.account_group_name, row.name];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return '';
}

function normalizeBrandMetricRow(
  row: Record<string, unknown>,
  bigQueryTotalAlias: string
): BrandMetrics {
  const brand = pickSocialMetricsSeriesLabel(row);
  const totalRaw = row.total ?? row[bigQueryTotalAlias];
  const n = typeof totalRaw === 'number' ? totalRaw : Number(totalRaw);
  return { brand, total: Number.isFinite(n) ? n : 0 };
}

function normalizeSocialMetricsComparisonResponse(
  raw: SocialMetricsComparisonResponse
): SocialMetricsComparisonResponse {
  const metricKeys = [
    'likes',
    'views',
    'comments',
    'engagement',
    'shares',
    'reposts',
    'saves',
  ] as const satisfies readonly (keyof SocialMetricsComparisonResponse)[];
  const result = {} as SocialMetricsComparisonResponse;
  for (const key of metricKeys) {
    const rows = raw[key];
    const bqAlias = bigQueryTotalFieldName(key);
    result[key] = Array.isArray(rows)
      ? rows.map(r => normalizeBrandMetricRow(r as unknown as Record<string, unknown>, bqAlias))
      : [];
  }
  return result;
}

export const getShareOfMetrics = async (
  params: ShareOfMetricsParams
): Promise<ShareOfMetricsData> => {
  validateRequiredParams(params, ['tenant_code', 'account_group_code']);

  const { tenant_code, account_group_code, ...queryParams } = params;

  const response = await apiClient.get<ShareOfMetricsResponse>(
    `/media/${encodeURIComponent(tenant_code)}/share-of-metrics/${encodeURIComponent(
      account_group_code
    )}`,
    { params: queryParams }
  );

  return response.data.data;
};

export const getSocialMetricsComparison = async (
  params: SocialMetricsComparisonParams
): Promise<SocialMetricsComparisonResponse> => {
  validateRequiredParams(params, ['tenant_code']);

  const { tenant_code, ...queryParams } = params;

  const response = await apiClient.get<SocialMetricsComparisonResponse>(
    `/media/${encodeURIComponent(tenant_code)}/social-metrics-comparison`,
    { params: queryParams }
  );

  const body = response.data;
  if (!body || typeof body !== 'object') {
    return {
      likes: [],
      views: [],
      comments: [],
      engagement: [],
      shares: [],
      reposts: [],
      saves: [],
    };
  }

  return normalizeSocialMetricsComparisonResponse(body);
};
